import { useState, type FormEvent } from 'react';
import type { MealSuggestion } from '../domain/meals';
import { type InventorySnapshot, type QuantityUnit } from '../domain/model';
import { useI18n } from '../i18n/I18nContext';
import { mealTemplateTitle } from '../i18n/domain-labels';
import { Button, containerDisplayName, containerReference, Field, Icon, locationLabel, Modal, unitLabel } from './components';

export interface ConfirmedRecipeLine {
  containerId: string;
  amount: number;
  unit: QuantityUnit;
}

interface EditableLine {
  containerId: string;
  checked: boolean;
  amount: string;
}

const initialLines = (suggestion: MealSuggestion): EditableLine[] =>
  suggestion.containers
    .filter((container) => container.remainingQuantity != null && container.unit != null)
    .map((container) => {
      const proposed = suggestion.deductions
        .filter((deduction) => deduction.container.id === container.id)
        .reduce((total, deduction) => total + deduction.amount, 0);
      return {
        containerId: container.id,
        checked: proposed > 0,
        amount: proposed > 0 ? String(Math.round(proposed * 1_000) / 1_000) : '',
      };
    });

export const MealPreparationModal = ({
  suggestion,
  snapshot,
  busy,
  onClose,
  onConfirm,
}: {
  suggestion: MealSuggestion;
  snapshot: InventorySnapshot;
  busy: boolean;
  onClose: () => void;
  onConfirm: (lines: ConfirmedRecipeLine[]) => Promise<void>;
}) => {
  const { formatNumber, locale, t, tp } = useI18n();
  const [lines, setLines] = useState<EditableLine[]>(() => initialLines(suggestion));
  const resolved = lines.map((line) => {
    const container = suggestion.containers.find((item) => item.id === line.containerId)!;
    const amount = Number(line.amount.replace(',', '.'));
    const valid =
      !line.checked ||
      (Number.isFinite(amount) &&
        amount > 0 &&
        container.remainingQuantity != null &&
        amount <= container.remainingQuantity);
    return { line, container, amount, valid };
  });
  const selected = resolved.filter((item) => item.line.checked);
  const canConfirm = selected.length > 0 && selected.every((item) => item.valid);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canConfirm) return;
    await onConfirm(
      selected.map(({ container, amount }) => ({
        containerId: container.id,
        amount,
        unit: container.unit!,
      })),
    );
  };

  return (
    <Modal
      title={t('meal.afterPreparation', { title: mealTemplateTitle(suggestion.template, locale) })}
      description={tp('meal.portionsDeduction', suggestion.portions)}
      onClose={onClose}
      wide
    >
      <form className="form-stack recipe-confirmation" onSubmit={(event) => void submit(event)}>
        <div className="notice notice--info">
          <Icon name="info" />
          <p>{t('meal.confirmationHelp')}</p>
        </div>
        {resolved.length === 0 ? (
          <div className="notice notice--warning">
            <Icon name="warning" />
            <p>{t('meal.noUsableQuantity')}</p>
          </div>
        ) : (
          <div className="recipe-consumption-lines">
            {resolved.map(({ line, container, valid }) => (
              <article key={container.id} className={line.checked ? 'is-selected' : ''}>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={line.checked}
                    onChange={(event) => setLines((current) =>
                      current.map((item) =>
                        item.containerId === container.id
                          ? { ...item, checked: event.target.checked }
                          : item,
                      ),
                    )}
                  />
                  <span>
                    <strong>{containerReference(container, locale)}{t('common.separator')}{containerDisplayName(snapshot, container, locale)}</strong>
                    <small>{locationLabel(snapshot, container.locationId, locale)}</small>
                  </span>
                </label>
                <Field
                  label={t('meal.actualQuantity', { unit: unitLabel(container.unit!, locale, Number(line.amount) || 2) })}
                  inputMode="decimal"
                  value={line.amount}
                  disabled={!line.checked}
                  min="0"
                  max={container.remainingQuantity ?? undefined}
                  step="any"
                  {...(!valid
                    ? { error: t('meal.maximumAvailable', { quantity: formatNumber(container.remainingQuantity ?? 0), unit: unitLabel(container.unit!, locale, container.remainingQuantity ?? 2) }) }
                    : {})}
                  onChange={(event) => setLines((current) =>
                    current.map((item) =>
                      item.containerId === container.id
                        ? { ...item, amount: event.target.value }
                        : item,
                    ),
                  )}
                />
              </article>
            ))}
          </div>
        )}
        <div className="button-stack">
          <Button type="submit" disabled={!canConfirm || busy} icon={<Icon name="check" />}>
            {t('meal.confirmQuantities')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>{t('meal.changeNothing')}</Button>
        </div>
      </form>
    </Modal>
  );
};
